import { useEffect, useMemo, useState } from 'react';
import { formatDateTime } from '../lib/datetime';
import TopBar from '../components/TopBar';
import { complianceService } from '../lib/services';
import { useApi } from '../lib/hooks';

/**
 * Compliance — live ACMA DNC + Spam Act consent + audit log feed.
 *
 *   • KPI strip  — counts from /v1/compliance/summary.
 *   • Table      — paged entries from /v1/compliance/audit, with a quick
 *                  action filter chip row.
 *
 * The page is fully driven by the backend; no design-data mocks remain.
 */

const ACTIONS = ['ALL', 'DNC', 'CONSENT', 'AUDIT', 'BLOCKED'];

export default function CompliancePage() {
  const [days, setDays]     = useState(30);
  const [actionTab, setTab] = useState('ALL');
  const [summary, setSummary] = useState(null);

  // Summary tiles — refresh whenever the window changes.
  useEffect(() => {
    let cancelled = false;
    complianceService.summary({ days })
      .then((s) => { if (!cancelled) setSummary(s); })
      .catch(() => { if (!cancelled) setSummary(null); });
    return () => { cancelled = true; };
  }, [days]);

  // Audit log — fetched via useApi so we get loading + refetch handles.
  const filterAction = actionTab === 'ALL' ? null
                     : actionTab === 'DNC' ? null      // server-side filter by exact action; we'll filter client-side
                     : actionTab === 'CONSENT' ? null
                     : actionTab === 'AUDIT' ? null
                     : null;

  const { data: log, loading, error, refetch } = useApi(
    () => complianceService.auditLog({ limit: 100, days, action: filterAction }),
    { fallback: [], deps: [days, actionTab] },
  );

  const rows = useMemo(() => {
    const all = Array.isArray(log) ? log : (log?.data ?? []);
    if (actionTab === 'ALL') return all;
    return all.filter((r) => {
      const a = String(r.action || '').toUpperCase();
      if (actionTab === 'DNC')      return a.includes('DNC');
      if (actionTab === 'CONSENT')  return a.includes('CONSENT');
      if (actionTab === 'BLOCKED')  return a.includes('BLOCK') || a.includes('FAIL') || a.includes('ERROR');
      if (actionTab === 'AUDIT')    return a.includes('AUDIT') || a.includes('REPLAY');
      return true;
    });
  }, [log, actionTab]);

  const kpis = [
    { l: 'ACMA DNC CHECKS', v: summary ? fmt(summary.dncChecks) : '—',     sub: `window ${days}d`,         c: 'success' },
    { l: 'DNC BLOCKED',     v: summary ? fmt(summary.dncBlocked) : '—',    sub: pct(summary?.dncBlocked, summary?.dncChecks), c: (summary?.dncBlocked || 0) > 0 ? 'warning' : 'success' },
    { l: 'CONSENT GRANTED', v: summary ? fmt(summary.consentGranted) : '—', sub: 'opt-ins captured',       c: 'success' },
    { l: 'CONSENT REVOKED', v: summary ? fmt(summary.consentRevoked) : '—', sub: 'opt-outs honoured',      c: 'success' },
    { l: 'AUDIT ENTRIES',   v: summary ? fmt(summary.auditEntries) : '—',   sub: `window ${days}d`,        c: 'success' },
    { l: 'AUDIT FAILURES',  v: summary ? fmt(summary.auditFailures) : '—',  sub: 'BLOCKED + FAILED rows',  c: (summary?.auditFailures || 0) > 0 ? 'danger' : 'success' },
  ];

  return (
    <>
      <TopBar
        crumbs={['Compliance']}
        right={
          <>
            <button className="btn small">Download audit pack</button>
            <button className="btn small" onClick={refetch}>{loading ? '…' : '↻ Refresh'}</button>
            <div className="period-toggle" role="tablist" aria-label="Compliance window">
              {[7, 30, 90].map((d) => (
                <button
                  key={d}
                  role="tab"
                  aria-selected={days === d}
                  className={days === d ? 'active' : ''}
                  onClick={() => setDays(d)}
                >{d}d</button>
              ))}
            </div>
          </>
        }
      />
      <div className="app-content">
        {/* KPI strip */}
       <div className="grid-6" style={{ marginBottom: 16 }}>
          {kpis.map((k, i) => (
            <div key={i} className="card animate-in" style={{ animationDelay: `${i * 0.04}s` }}>
              <div className="card-label">{k.l}</div>
              <div className="metric-value" style={{ fontSize: 24, color: `var(--${k.c})` }}>{k.v}</div>
              <div className="mono text-xs text-muted">{k.sub}</div>
            </div>
          ))}
        </div>

        {/* Action filter chips */}
        <div className="flex-row" style={{ marginBottom: 12, gap: 6 }}>
          <span className="card-label" style={{ marginRight: 8 }}>FILTER:</span>
          {ACTIONS.map((a) => (
            <span
              key={a}
              className={`pill ${actionTab === a ? 'active' : ''}`}
              style={{ cursor: 'pointer' }}
              onClick={() => setTab(a)}
            >{a}</span>
          ))}
          <span className="mono text-xs text-muted" style={{ marginLeft: 'auto' }}>
            {rows.length} entr{rows.length === 1 ? 'y' : 'ies'}
          </span>
        </div>

        {/* Audit table */}
        <div className="card animate-in" style={{ animationDelay: '0.3s', padding: 0, overflow: 'hidden' }}>
          <div className="card-label" style={{ padding: '14px 14px 8px' }}>
            DNC + SPAM ACT AUDIT LOG · {error ? 'BACKEND UNAVAILABLE' : 'LIVE'}
          </div>
          {rows.length === 0 ? (
            <div className="empty-state" style={{ padding: 28 }}>
              <div className="title">No audit entries</div>
              <div className="desc mono text-xs text-muted">
                Outbound traffic, consent events, and DNC gate decisions will
                appear here as they flow.
              </div>
            </div>
          ) : (
            <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>{['Time', 'Actor', 'Action', 'Entity', 'Target', 'Severity', 'Audit ID'].map(h => <th key={h}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="mono text-xs">{formatDateTime(r.timestamp)}</td>
                    <td className="mono text-xs">{r.actorName || '—'}</td>
                    <td className="mono text-xs" style={{ fontWeight: 600 }}>{r.action}</td>
                    <td className="mono text-xs text-muted">{r.entityType || '—'}</td>
                    <td className="mono text-xs">{r.targetRef || '—'}</td>
                    <td>
                      <span className={`pill ${r.severity || ''} ${r.severity ? 'filled' : ''}`} style={{ fontSize: 10 }}>
                        {(r.severity || 'info').toUpperCase()}
                      </span>
                    </td>
                    <td className="mono text-xs" style={{ color: 'var(--info)' }}>{r.id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function fmt(n) {
  if (n == null) return '—';
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return String(n);
}

function pct(num, den) {
  if (!den || den === 0) return '0%';
  return `${((num / den) * 100).toFixed(2)}% of total`;
}
